export type AdminSound = 'click' | 'notification' | 'success' | 'warning' | 'error';

// 기존 상태별 매핑과 사용자가 지정한 일반 알림 음원을 사용한다.
export const ADMIN_SOUNDS: Record<AdminSound, { file: string; volume: number; priority: number }> = {
  click: { file: 'universfield-computer-mouse-click-352734.mp3', volume: 0.10, priority: 0 },
  notification: { file: 'notification.mp3', volume: 0.12, priority: 1 },
  success: { file: 'updatepelgo-success-221935.mp3', volume: 0.20, priority: 2 },
  warning: { file: 'semi-error-notification-05-199276.mp3', volume: 0.18, priority: 2 },
  error: { file: 'universfield-wrong-answer-126515.mp3', volume: 0.20, priority: 3 },
};
export const SOUND_PREFERENCE_KEY = 'rnf-admin-sound-enabled';

export class AdminSoundManager {
  private enabled = true;
  private active = 0;
  private interacted = false;
  private listeners = new Set<() => void>();
  private audio = new Map<string, HTMLAudioElement>();
  private last = new Map<AdminSound, number>();
  private current?: HTMLAudioElement;
  private priority = -1;
  private clickTimer?: ReturnType<typeof setTimeout>;
  readonly subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  readonly getEnabled = () => this.enabled;
  setEnabled(enabled: boolean, persist = true): void {
    this.enabled = enabled;
    if (!enabled) this.stop();
    if (persist) { try { window.localStorage.setItem(SOUND_PREFERENCE_KEY, String(enabled)); } catch { /* 저장 불가 시 현재 탭만 적용한다. */ } }
    this.listeners.forEach(listener => listener());
  }
  activate(): () => void {
    this.active++;
    try { this.setEnabled(window.localStorage.getItem(SOUND_PREFERENCE_KEY) !== 'false', false); } catch { /* 기본 설정 유지 */ }
    return () => {
      this.active = Math.max(0, this.active - 1);
      if (!this.active) { this.stop(); this.interacted = false; this.last.clear(); }
    };
  }
  interact(): void { if (this.active) this.interacted = true; }
  private stop(): void {
    clearTimeout(this.clickTimer);
    try { this.current?.pause(); } catch { /* 업무 동작에 영향을 주지 않는다. */ }
    this.current = undefined; this.priority = -1;
  }
  play(kind: AdminSound): void {
    if (!this.active || !this.interacted || !this.enabled) return;
    if (kind === 'click') {
      clearTimeout(this.clickTimer);
      this.clickTimer = setTimeout(() => this.playNow(kind), 35);
    } else { clearTimeout(this.clickTimer); this.playNow(kind); }
  }
  private playNow(kind: AdminSound): void {
    const config = ADMIN_SOUNDS[kind], now = Date.now();
    if (!this.active || !this.enabled || !this.interacted || config.priority < this.priority
      || now - (this.last.get(kind) ?? -Infinity) < (kind === 'click' ? 140 : 600)) return;
    this.last.set(kind, now);
    try {
      this.stop();
      let audio = this.audio.get(config.file);
      if (!audio) { audio = new Audio(`/sound/${config.file}`); audio.preload = 'auto'; this.audio.set(config.file, audio); }
      audio.volume = config.volume; audio.currentTime = 0;
      this.current = audio; this.priority = config.priority;
      const finished = () => { if (this.current === audio) this.stop(); };
      audio.onended = finished;
      // 재생은 음원 끝까지 이어지고, UI 동작은 재생 완료를 기다리지 않는다.
      void audio.play().catch(finished);
    } catch { this.stop(); }
  }
}
export const adminSound = new AdminSoundManager();
